import React, { useEffect, useState } from 'react';
import { useSite } from '../../../context/SiteContext';
import { fetchStats } from '../../../lib/api';
import { ArrowRight, TrendingUp, Calendar } from 'lucide-react';

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="text-zinc-500 text-xs font-semibold">{label}</div>
      <div className={`mt-1 text-2xl md:text-3xl font-bold ${accent}`}>{value}</div>
      {sub && <div className="text-zinc-500 text-[11px] mt-1">{sub}</div>}
    </div>
  );
}

function Chart({ series }) {
  if (!series || series.length === 0) return null;
  const max = Math.max(1, ...series.map((d) => d.count));
  const W = 600, H = 140, pad = 24;
  const innerW = W - pad * 2, innerH = H - pad * 2;
  const stepX = innerW / Math.max(1, series.length - 1);
  const points = series.map((d, i) => {
    const x = pad + i * stepX;
    const y = pad + innerH - (d.count / max) * innerH;
    return [x, y, d];
  });
  const path = points.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${path} L${pad + innerW},${pad + innerH} L${pad},${pad + innerH} Z`;
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[480px]">
        <defs>
          <linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#d4d77a" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#d4d77a" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <line key={i} x1={pad} x2={pad + innerW} y1={pad + p * innerH} y2={pad + p * innerH} stroke="#27272a" strokeWidth="1" strokeDasharray="3 4" />
        ))}
        <path d={area} fill="url(#chartFill)" />
        <path d={path} fill="none" stroke="#d4d77a" strokeWidth="2" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="3" fill="#d4d77a" />
            {p[2].count > 0 && <text x={p[0]} y={p[1] - 6} fontSize="9" fill="#d4d77a" textAnchor="middle">{p[2].count}</text>}
          </g>
        ))}
        {points.filter((_, i) => i % 2 === 0).map((p, i) => (
          <text key={i} x={p[0]} y={H - 6} fontSize="9" fill="#6b7280" textAnchor="middle">
            {new Date(p[2].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </text>
        ))}
      </svg>
    </div>
  );
}

const quickLinks = [
  { key: 'leads', label: 'View Enquiries & Leads', desc: 'All user enquiries' },
  { key: 'products', label: 'Manage Products', desc: 'B2C / Line / B2B catalog' },
  { key: 'branding', label: 'Edit Brand & Colors', desc: 'Logo, theme, gradients' },
  { key: 'hero', label: 'Hero Slides', desc: 'Banner titles, images' },
  { key: 'marquee', label: 'Logo Marquee & Social', desc: 'Brand strip & socials' },
  { key: 'settings', label: 'Settings', desc: 'Change password, reset' }
];

export default function DashboardSection({ setActive }) {
  const { data } = useSite();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => { /* noop */ });
  }, []);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-zinc-800 bg-gradient-to-br from-[color:var(--gold)]/10 via-zinc-950 to-zinc-950 p-5 md:p-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-zinc-100">Welcome back, Admin</h2>
          <p className="text-zinc-400 text-sm mt-1">Live overview of your marketplace.</p>
        </div>
        <button onClick={() => setActive('leads')} className="h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm flex items-center gap-2" data-testid="dashboard-view-leads-btn">
          View Enquiries & Leads <ArrowRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Today" value={stats?.today ?? '—'} accent="text-emerald-300" sub="new enquiries" />
        <StatCard label="Yesterday" value={stats?.yesterday ?? '—'} accent="text-amber-300" sub="new enquiries" />
        <StatCard label="All-time Leads" value={stats?.all_time ?? '—'} accent="text-sky-300" sub="total" />
        <StatCard label="B2C" value={stats?.b2c ?? '—'} accent="text-rose-300" sub="category leads" />
        <StatCard label="B2B" value={stats?.b2b ?? '—'} accent="text-violet-300" sub="category leads" />
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 md:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-[color:var(--gold)]" />
            <h3 className="text-zinc-100 font-bold text-sm">Enquiries — last 14 days</h3>
          </div>
          <span className="text-zinc-500 text-xs flex items-center gap-1"><Calendar size={12} /> Daily trend</span>
        </div>
        <Chart series={stats?.series || []} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {quickLinks.map((q) => (
          <button key={q.key} onClick={() => setActive(q.key)} className="text-left rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 hover:border-[color:var(--gold)]/40 hover:bg-zinc-900/60 transition">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-zinc-100 font-bold text-sm">{q.label}</div>
                <div className="text-zinc-500 text-xs mt-0.5">{q.desc}</div>
              </div>
              <ArrowRight size={16} className="text-zinc-500" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
